import { useEffect, useRef } from "react";

import { getPosRuntimePerformanceProfile } from "../performance/runtime-performance-profile";

export function useRuntimeAutoSync({
  enabled,
  sync,
  triggerKey,
  intervalMs,
}: {
  enabled: boolean;
  sync: () => Promise<void>;
  triggerKey?: string;
  intervalMs?: number;
}) {
  const syncRef = useRef(sync);
  const inFlightRef = useRef(false);
  const effectiveIntervalMs = intervalMs ?? getPosRuntimePerformanceProfile().backgroundSyncIntervalMs;

  useEffect(() => {
    syncRef.current = sync;
  }, [sync]);

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;

    const run = async () => {
      if (disposed || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await syncRef.current();
      } finally {
        inFlightRef.current = false;
      }
    };

    const runWhenVisible = () => {
      if (document.visibilityState === "visible") void run();
    };

    // Bootstrap immediately and still refresh on focus/online/foreground. Only the periodic
    // background cadence is relaxed for constrained runtimes so local sale interaction keeps
    // main-thread priority without making the POS network-first or delaying explicit commits.
    void run();
    const interval = window.setInterval(runWhenVisible, effectiveIntervalMs);
    window.addEventListener("focus", runWhenVisible);
    window.addEventListener("online", runWhenVisible);
    document.addEventListener("visibilitychange", runWhenVisible);

    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", runWhenVisible);
      window.removeEventListener("online", runWhenVisible);
      document.removeEventListener("visibilitychange", runWhenVisible);
    };
  }, [effectiveIntervalMs, enabled, triggerKey]);
}
